// What the duplicate compare screen says about a flagged draft and the
// finalized document it collides with (GET /documents/:id → duplicate_of).
//
// The server flags a duplicate on (supplier, doc number) alone. Whether the
// draft really IS the same document is the admin's call, and they make it on
// two things: the scans look alike, and the money matches. This module does
// the money/date/lines part so the banner can say "סכום זהה · תאריך זהה" before
// anyone clicks, and the dialog can colour each field. Pure, no DOM.

const sameMoney = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.005;
const sameText = (a, b) => String(a || '').trim() === String(b || '').trim();

// Lines on the draft come as `items` (the page has them); on the existing
// document as `item_count` (the compare row carries only the count).
export const lineCount = (doc) => (
  Array.isArray(doc?.items) ? doc.items.length : (Number.isInteger(doc?.item_count) ? doc.item_count : null)
);

// Every field the dialog's diff bar shows, in display order. `same` is null
// when one side is unknown (a line count the server didn't send).
export function compareDuplicate(draft, existing) {
  const dl = lineCount(draft);
  const el = lineCount(existing);
  return [
    { key: 'total', label: 'סה״כ כולל מע״מ', kind: 'money', draft: draft?.total, existing: existing?.total, same: sameMoney(draft?.total, existing?.total) },
    { key: 'subtotal', label: 'לפני מע״מ', kind: 'money', draft: draft?.subtotal, existing: existing?.subtotal, same: sameMoney(draft?.subtotal, existing?.subtotal) },
    { key: 'date', label: 'תאריך', kind: 'date', draft: draft?.date, existing: existing?.date, same: sameText(draft?.date, existing?.date) },
    { key: 'doc_type', label: 'סוג מסמך', kind: 'text', draft: draft?.doc_type, existing: existing?.doc_type, same: sameText(draft?.doc_type, existing?.doc_type) },
    { key: 'lines', label: 'שורות', kind: 'count', draft: dl, existing: el, same: dl == null || el == null ? null : dl === el },
  ];
}

// The three chips the banner shows before any click: money, date, lines.
// A null `same` (unknown line count) yields no chip rather than a wrong one.
export function duplicateSignals(draft, existing) {
  const by = Object.fromEntries(compareDuplicate(draft, existing).map(f => [f.key, f]));
  const chips = [
    { key: 'total', same: by.total.same, label: by.total.same ? 'סכום זהה' : 'סכום שונה' },
    { key: 'date', same: by.date.same, label: by.date.same ? 'תאריך זהה' : 'תאריך שונה' },
  ];
  if (by.lines.same != null) {
    chips.push({
      key: 'lines', same: by.lines.same,
      label: by.lines.same ? `${by.lines.draft} שורות · זהה` : `שורות: ${by.lines.draft} בטיוטה · ${by.lines.existing} בקיים`,
    });
  }
  return chips;
}

// One short pill for the dialog header (it has to fit a phone beside the
// title). Counts only the fields that could be compared: "5/5 זהה" when
// everything matches, otherwise how many differ.
export function duplicateVerdict(fields) {
  const known = fields.filter(f => f.same != null);
  const differ = known.filter(f => !f.same).length;
  if (known.length === 0) return { strong: false, label: 'אין מספיק נתונים להשוואה' };
  if (differ === 0) return { strong: true, label: `${known.length}/${known.length} זהה · כנראה כפיל` };
  return { strong: false, label: `${differ}/${known.length} שונים · כנראה מסמך אחר` };
}

// "השאר את הטיוטה · מחק את הקיים" (owner, 14/10/2026): sometimes the draft is
// the copy to keep and the finalized twin is the one to throw away. Both share
// the number, so the delete dialog's generic "למחוק את המסמך ...?" can't tell
// them apart — these name the existing document by id, status and total, say
// the draft stays, and warn about what leaves with it: payments recorded on it
// leave the payment screens (a restore brings them back; nothing moves them to
// the draft — owner's call), and its links to other documents are detached
// (lib/softDelete.js). `formatMoney` is passed in to keep this module free of
// the API layer.
export function keepDraftDeleteQuestion(existing, formatMoney) {
  const facts = [existing?.status, existing?.total != null ? formatMoney(existing.total) : null].filter(Boolean).join(' · ');
  return `למחוק את המסמך הקיים #${existing?.id}${facts ? ` (${facts})` : ''}?`;
}

export const keepDraftDeleteContext = (draftId) => `הטיוטה #${draftId} תישאר ותיפתח לעריכה.`;

// null when nothing but the document itself leaves.
export function keepDraftDeleteWarning(existing, formatMoney) {
  const lines = [];
  const pays = Number(existing?.payment_count) || 0;
  if (pays === 1) {
    lines.push(`למסמך הקיים רשום תשלום בסך ${formatMoney(existing.payment_paid_total)} — הוא יירד ממסכי התשלומים יחד איתו. אחרי אישור הטיוטה יש לרשום אותו עליה מחדש.`);
  } else if (pays > 1) {
    lines.push(`למסמך הקיים רשומים ${pays} תשלומים בסך כולל ${formatMoney(existing.payment_paid_total)} — הם יירדו ממסכי התשלומים יחד איתו. אחרי אישור הטיוטה יש לרשום אותם עליה מחדש.`);
  }
  const links = Number(existing?.link_count) || 0;
  if (links === 1) {
    lines.push('המסמך הקיים מקושר למסמך אחר — הקישור ינותק. אחרי אישור הטיוטה יש לקשר אותו אליה מחדש.');
  } else if (links > 1) {
    lines.push(`המסמך הקיים מקושר ל-${links} מסמכים — הקישורים ינותקו. אחרי אישור הטיוטה יש לקשר אותם אליה מחדש.`);
  }
  return lines.length ? lines.join(' ') : null;
}
